import { Formik, FormikProps } from 'formik';
import i18n from 'i18next';
import { FC, Fragment } from 'react';

import { FieldCard, FormField } from '@components';
import { getProfileFormConfig } from '@constants';
import { initData, updateDataInStorage } from '@core/storage';
import { DisplayMode, Language } from '@enums';
import { useUserDetails } from '@store';
import type { FormFieldProps, ProfileSettingsData, ProfileSettingsFormProps } from '@types';

export const ProfileSettingsForm: FC<ProfileSettingsFormProps> = ({
  handleVerifyDialog,
  handleChangeUsername,
  handleChangePassword,
}) => {
  const init = initData.getItem();
  const userDetails = useUserDetails();

  const initialValues: ProfileSettingsData = {
    email: userDetails?.email ?? '',
    firstName: userDetails?.first_name ?? '',
    lastName: userDetails?.last_name ?? '',
    displayMode: init?.displayMode ?? DisplayMode.Light,
    language: init?.language ?? Language.English,
    status: userDetails?.status ?? 0,
  };

  const handleSelectChange = (formik: FormikProps<ProfileSettingsData>, name: string, value: string) => {
    formik.setFieldValue(name, value);

    if (name === 'language') {
      updateDataInStorage(initData, ['language'], { language: value });
      i18n.changeLanguage(value);
    }
  };

  const renderFormField = (field: FormFieldProps, formikValues: ProfileSettingsData) => {
    const { name, label, description, ...props } = field;

    return (
      <FieldCard key={name} title={field.title ?? label} description={description}>
        <FormField name={name} label={label} key={name} {...props} />
      </FieldCard>
    );
  };

  return (
    <Formik<ProfileSettingsData> initialValues={initialValues} enableReinitialize onSubmit={() => {}}>
      {(formik) => {
        const { email, firstName, lastName, status } = formik.values;

        const hasUsername = firstName || lastName;
        const username = hasUsername ? `${firstName} ${lastName}` : '—';

        const profileSettingsFields = getProfileFormConfig({
          isVerified: status === 1,
          email,
          username,
          handleChangeUsername,
          handleChangePassword,
          handleModeChange: (e) => handleSelectChange(formik, 'displayMode', e.target.value),
          handleLanguageChange: (e) => handleSelectChange(formik, 'language', e.target.value),
        })?.inputList;

        return (
          <Fragment>{profileSettingsFields.map((field, index) => renderFormField(field, formik.values))}</Fragment>
        );
      }}
    </Formik>
  );
};
